(() => {
  'use strict';

  const initialize = () => {
    const root = document.documentElement;
    if (root.hasAttribute('data-rudca-header-chat-ready')) return;
    root.setAttribute('data-rudca-header-chat-ready', '');

    const controlSelector = '[data-rudca-header-chat]';
    const itemSelector = '[data-rudca-header-chat-item]';
    const hasContactFallback = (control) => control instanceof HTMLAnchorElement && control.hasAttribute('href') && control.hasAttribute('data-rudca-chat-fallback-label');
    const marker = 'data-rudca-chat-in-header';
    const protectedSelector = '#mini-cart[aria-hidden="false"], #rudca-quantity-dialog[open], .pswp[aria-hidden="false"]';
    let provider = null;
    let opener = null;
    let wasOpen = false;
    let failed = false;
    let pageHidden = false;
    let frame = 0;
    let retry = 0;
    let retryUntil = Date.now() + 30000;
    let verifyTimer = 0;
    let settleTimer = 0;
    let observedRoots = [];

    const setAttribute = (element, name, value) => {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    };
    const schedule = () => {
      if (!pageHidden && !frame) frame = requestAnimationFrame(sync);
    };
    const observer = new MutationObserver(schedule);

    const discover = () => {
      const roots = [root];
      const providers = [];
      document.querySelectorAll('shopify-chat').forEach((host) => {
        const hostRoot = host.shadowRoot;
        if (!hostRoot) return;
        roots.push(hostRoot);
        const runtime = hostRoot.querySelector('shopify-agent-runtime');
        const runtimeRoot = runtime?.shadowRoot;
        if (!runtimeRoot) return;
        roots.push(runtimeRoot);
        const parts = (runtime.getAttribute('exportparts') || '').split(',').map((part) => part.trim());
        const button = runtimeRoot.querySelector('button[part~="activator"]');
        const dialog = runtimeRoot.querySelector('[part~="dialog"][role="dialog"]');
        if (parts.includes('activator') && parts.includes('dialog') &&
          button instanceof HTMLButtonElement && !button.disabled &&
          ['true', 'false'].includes(button.getAttribute('aria-expanded')) && dialog) {
          providers.push({ host, runtime, button, dialog });
        }
      });
      if (roots.length !== observedRoots.length || roots.some((node, index) => node !== observedRoots[index])) {
        observer.disconnect();
        roots.forEach((node) => observer.observe(node, {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['open', 'inert', 'aria-expanded', 'aria-hidden', 'disabled', 'exportparts', 'data-rudca-chat-paused']
        }));
        observedRoots = roots;
      }
      // An ambiguous second provider is deliberately left to its native UI.
      return providers.length === 1 ? providers[0] : null;
    };

    const isOpen = (current) => Boolean(current &&
      current.host.hasAttribute('open') && current.runtime.hasAttribute('open') &&
      current.button.getAttribute('aria-expanded') === 'true' &&
      !current.dialog.hasAttribute('inert'));

    const sync = () => {
      frame = 0;
      const previousProvider = provider;
      provider = discover();
      // Native Close can replace its runtime long after initial page load.
      // Reopen a bounded discovery window when a known provider disappears;
      // shadow-root attachment itself is invisible to a light-DOM observer.
      if (previousProvider && !provider) retryUntil = Date.now() + 30000;
      const controls = [...document.querySelectorAll(controlSelector)];
      // Reconcile the SSR guarantee against the actual support link, never
      // against transient provider availability. A real section removal must
      // restore the native launcher; provider recreation must not flash it.
      const fallback = controls.find(hasContactFallback);
      root.toggleAttribute('data-rudca-header-support', Boolean(fallback));
      const ready = Boolean(provider && controls.length && !failed);
      const blocked = Boolean(document.querySelector(protectedSelector));
      const open = ready && isOpen(provider);
      document.querySelectorAll(`shopify-chat[${marker}]`).forEach((host) => {
        if (!ready || host !== provider.host) host.removeAttribute(marker);
      });
      controls.forEach((control) => {
        const item = control.closest(itemSelector);
        const fallback = hasContactFallback(control);
        // Keep the server-rendered contact link and its 44px slot stable even
        // while Shopify loads, replaces its runtime, or cannot open a dialog.
        if (item) item.hidden = !fallback && !ready;
        if (control instanceof HTMLButtonElement && control.disabled !== blocked) control.disabled = blocked;
        if (blocked) setAttribute(control, 'aria-disabled', 'true');
        else control.removeAttribute('aria-disabled');
        if (ready) {
          if (fallback) setAttribute(control, 'role', 'button');
          setAttribute(control, 'aria-haspopup', 'dialog');
          setAttribute(control, 'aria-expanded', String(Boolean(open && !blocked && !root.hasAttribute('data-rudca-chat-paused'))));
        } else if (fallback) {
          control.removeAttribute('role');
          control.removeAttribute('aria-haspopup');
          control.removeAttribute('aria-expanded');
        }
        // No claim that staff are online. With no provider, this is an ordinary
        // contact-page link; it never queues a later surprise chat opening.
        const label = ready ? provider.button.getAttribute('aria-label') : control.getAttribute('data-rudca-chat-fallback-label');
        if (label) {
          setAttribute(control, 'aria-label', label);
          if (fallback) setAttribute(control, 'title', label);
        }
      });
      if (ready) provider.host.toggleAttribute(marker, true);
      if (fallback) {
        document.querySelectorAll('[data-rudca-cart-widget-tools] [data-rudca-show-widgets]').forEach((control) => {
          const label = ready
            ? control.getAttribute(control.getAttribute('aria-expanded') === 'true' ? 'data-rudca-hide-label' : 'data-rudca-show-label')
            : fallback.getAttribute('data-rudca-chat-fallback-label');
          if (label && control.textContent !== label) control.textContent = label;
          if (!ready) setAttribute(control, 'aria-expanded', 'false');
        });
      }

      if (open && verifyTimer && !settleTimer) {
        // Success must survive the entire originating click event. An outside-
        // click handler can close a synchronously opened dialog later in that
        // same dispatch; do not cancel fallback verification prematurely.
        settleTimer = setTimeout(() => {
          settleTimer = 0;
          const settled = discover();
          if (isOpen(settled)) { clearTimeout(verifyTimer); verifyTimer = 0; }
        }, 0);
      }
      // Native Close can replace the whole runtime. Only an observed, settled
      // closed provider ends the session; a transient missing node does not.
      if (wasOpen && provider && !isOpen(provider)) {
        wasOpen = false;
        document.dispatchEvent(new CustomEvent('rudca:chat:closed'));
        if (opener?.isConnected && !blocked) opener.focus({ preventScroll: true });
        opener = null;
      } else if (open) {
        wasOpen = true;
      }

      clearTimeout(retry);
      retry = 0;
      // Shadow roots may attach after the custom element is registered, which
      // does not emit a light-DOM mutation. A bounded bootstrap catches that;
      // if it never loads, the original launcher remains the fallback.
      if (!provider && !failed && Date.now() < retryUntil && !pageHidden) {
        retry = setTimeout(schedule, 500);
      }
    };

    const restoreNative = () => {
      failed = true;
      clearTimeout(verifyTimer);
      verifyTimer = 0;
      sync();
      // A real contact link remains in the same header position on failure.
      // Without that server-rendered fallback, retain the native launcher.
      const fallback = [...document.querySelectorAll(controlSelector)].find(hasContactFallback);
      if (!document.querySelector(protectedSelector)) {
        if (fallback) fallback.focus({ preventScroll: true });
        else if (provider) provider.button.focus({ preventScroll: true });
      }
      opener = null;
    };

    const toggle = (control) => {
      sync();
      if (!provider || failed || control.disabled || pageHidden || document.querySelector(protectedSelector)) return false;
      const opening = !isOpen(provider);
      const resuming = root.hasAttribute('data-rudca-chat-paused');
      opener = control;
      if (opening || resuming) document.dispatchEvent(new CustomEvent('rudca:chat:request-open'));
      if (root.hasAttribute('data-rudca-chat-paused')) return false;
      // Back/Forward or the cart's old hide control can pause an open panel.
      // Reveal the existing conversation instead of toggling it closed.
      if (!opening && resuming) { sync(); return true; }
      clearTimeout(verifyTimer);
      try {
        // Click only the verified native DOM button. No private method, event
        // name, injected message, iframe rewrite or account-setting change.
        provider.button.click();
      } catch (_) {
        restoreNative();
        return true;
      }
      if (opening) {
        verifyTimer = setTimeout(() => {
          sync();
          if (!isOpen(provider)) restoreNative();
        }, 2000);
      }
      sync();
      return true;
    };

    document.addEventListener('click', (event) => {
      // A click inside the open native dialog proves the opening succeeded,
      // including a very fast intentional Close before the settle timer runs.
      // Native dialog events continue unchanged.
      if (verifyTimer && isOpen(provider) && event.composedPath().includes(provider.dialog)) {
        clearTimeout(verifyTimer);
        verifyTimer = 0;
      }
      const target = event.target instanceof Element ? event.target : null;
      const control = target?.closest(`${controlSelector}, [data-rudca-cart-widget-tools] [data-rudca-show-widgets]`);
      if (!control || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      sync();
      if (document.querySelector(protectedSelector)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (pageHidden) return;
      if (!provider || failed || control.disabled) {
        // The cart's existing button gets the same honest contact fallback.
        // The header anchor itself keeps its normal navigation and modifiers.
        const fallback = [...document.querySelectorAll(controlSelector)].find(hasContactFallback);
        if (fallback && control.matches('[data-rudca-show-widgets]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          fallback.click();
        }
        return;
      }
      // Contain only a usable chat trigger's original event, before dispatching
      // the native button click. Otherwise Shopify can read the header/cart
      // click as an outside click and immediately close the new conversation.
      // Capture also avoids the existing cart control double-toggling it.
      event.preventDefault();
      event.stopImmediatePropagation();
      toggle(control);
    }, true);
    document.addEventListener('keydown', (event) => {
      const control = event.target instanceof Element ? event.target.closest(controlSelector) : null;
      if (event.key !== ' ' || !control || control.getAttribute('role') !== 'button') return;
      event.preventDefault();
      if (!event.repeat) control.click();
    });
    document.addEventListener('rudca:chat:toggle', (event) => {
      const control = event.detail?.control;
      if (control instanceof HTMLButtonElement && control.matches('[data-rudca-show-widgets]') &&
        control.closest('[data-rudca-cart-widget-tools]') && toggle(control)) event.preventDefault();
    });

    document.addEventListener('shopify:section:load', () => {
      retryUntil = Date.now() + 30000;
      schedule();
    });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pagehide', () => {
      pageHidden = true;
      clearTimeout(retry);
      clearTimeout(verifyTimer);
      clearTimeout(settleTimer);
      settleTimer = 0;
      cancelAnimationFrame(frame);
      frame = 0;
    });
    window.addEventListener('pageshow', () => {
      pageHidden = false;
      failed = false;
      retryUntil = Date.now() + 30000;
      schedule();
    });
    if (window.customElements) {
      ['shopify-chat', 'shopify-agent-runtime'].forEach((name) => customElements.whenDefined(name).then(schedule));
    }
    sync();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
